import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';

class GispenMenu {
  /**
   * Base state
   */
  _state = {
    open: false,
    scrollTop: 0,
  };

  static options = {
    timeout: 450,
  };

  constructor(options = {}) {
    this._options = { ...options };
    this._elements = Array.prototype.slice.call(document.querySelectorAll('.side-toggle'), 0);
    this._target = document.querySelector('#side_menu');
    this._main = document.querySelector('#main');
    this._body = document.querySelector('#body');
    this._html = document.documentElement;
    this._mainNav = document.querySelector('.main-nav');

    if (this._body.getAttribute('data-edit') === 'true') {
      return;
    }

    this._navigation = this._target.querySelector('.navigation-list');
    this._backButton = this._target.querySelector('.side-menu__back__button');
    this._closeButtons = [...document.querySelectorAll('.close-sidemenu')];
    this._contactButtons = [...document.querySelectorAll('.contact-button')];

    this._closeButtons.forEach((element) => {
      element.onclick = () => {
        this.closeSideMenu(element);
      };
    });

    this._contactButtons.forEach((element) => {
      if (element.getAttribute('href').indexOf('#') > -1) {
        element.classList.add('anchor-link');
      } else {
        element.classList.remove('anchor-link');
      }
    });

    this._elements.forEach((element) => {
      element.onclick = () => {
        this._state.open = !this._state.open;
        if (!this._state.open) {
          // closing menu
          this.closeSideMenu(element);
        } else {
          // opening menu
          this.togglemenu(this, element);
        }
      };
    });

    // Initialize desktop menu functionality
    this._initializeDesktopMenu();

    // Add resize listener to recalculate submenu positions
    window.addEventListener('resize', () => {
      this._hideAllSubmenus();
    });
  }

  /**
   * Initialize desktop menu hover functionality
   */
  _initializeDesktopMenu() {
    if (!this._mainNav) return;

    const menuItems = this._mainNav.querySelectorAll('.list-item.has-submenu');

    menuItems.forEach((item) => {
      const submenu = item.querySelector('.submenu');
      const link = item.querySelector('.list-item__link');
      let hoverTimeout;

      // Mouse interactions
      item.addEventListener('mouseenter', () => {
        clearTimeout(hoverTimeout);
        // Close other top-level submenus
        this._closeOtherTopLevelSubmenus(item);

        // Check if this submenu should be collapsed
        if (this._shouldCollapseSubmenu(submenu)) {
          // For collapsed menus, we'll handle clicks instead of hover
          return;
        }

        this._showSubmenu(submenu);
        this._updateAriaExpanded(link, true);
      });

      item.addEventListener('mouseleave', () => {
        // Don't auto-hide collapsed submenus on mouse leave
        if (submenu && submenu.classList.contains('submenu--collapsed')) {
          return;
        }

        hoverTimeout = setTimeout(() => {
          this._hideSubmenu(submenu);
          this._updateAriaExpanded(link, false);
        }, 100);
      });

      // Click interactions for collapsed menus
      link.addEventListener('click', (e) => {
        if (this._shouldCollapseSubmenu(submenu)) {
          e.preventDefault();
          const isCurrentlyVisible = submenu.style.display === 'block';

          if (isCurrentlyVisible) {
            this._hideSubmenu(submenu);
            this._updateAriaExpanded(link, false);
          } else {
            this._closeOtherTopLevelSubmenus(item);
            this._showSubmenu(submenu);
            this._updateAriaExpanded(link, true);
          }
        }
      });

      // Keyboard interactions
      link.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown' || e.key === 'Enter') {
          e.preventDefault();
          // Close other top-level submenus first
          this._closeOtherTopLevelSubmenus(item);
          this._showSubmenu(submenu);
          this._updateAriaExpanded(link, true);
          // Focus first submenu item
          const firstSubmenuLink = submenu.querySelector('.submenu__link');
          if (firstSubmenuLink) {
            firstSubmenuLink.focus();
          }
        } else if (e.key === 'Escape') {
          e.preventDefault();
          this._hideAllSubmenus();
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          // Navigate to previous top-level item
          const topLevelItems = Array.from(this._mainNav.querySelectorAll('.list-item'));
          const currentIndex = topLevelItems.indexOf(item);
          const prevIndex = currentIndex === 0 ? topLevelItems.length - 1 : currentIndex - 1;
          const prevLink = topLevelItems[prevIndex].querySelector('.list-item__link');
          if (prevLink) {
            this._hideAllSubmenus();
            prevLink.focus();
          }
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          // Navigate to next top-level item
          const topLevelItems = Array.from(this._mainNav.querySelectorAll('.list-item'));
          const currentIndex = topLevelItems.indexOf(item);
          const nextIndex = (currentIndex + 1) % topLevelItems.length;
          const nextLink = topLevelItems[nextIndex].querySelector('.list-item__link');
          if (nextLink) {
            this._hideAllSubmenus();
            nextLink.focus();
          }
        }
      });

      // Handle nested submenus
      const nestedItems = item.querySelectorAll('.submenu__item.has-submenu');
      nestedItems.forEach((nestedItem) => {
        const nestedSubmenu = nestedItem.querySelector('.submenu');
        const nestedLink = nestedItem.querySelector('.submenu__link');
        let nestedHoverTimeout;

        nestedItem.addEventListener('mouseenter', () => {
          clearTimeout(nestedHoverTimeout);
          // Close sibling submenus at the same level
          this._closeSiblingSubmenus(nestedItem);

          // Check if this nested submenu should be collapsed
          if (this._shouldCollapseSubmenu(nestedSubmenu)) {
            // For collapsed nested menus, we'll handle clicks instead of hover
            return;
          }

          this._showSubmenu(nestedSubmenu);
          this._updateAriaExpanded(nestedLink, true);
        });

        nestedItem.addEventListener('mouseleave', () => {
          // Don't auto-hide collapsed submenus on mouse leave
          if (nestedSubmenu && nestedSubmenu.classList.contains('submenu--collapsed')) {
            return;
          }

          nestedHoverTimeout = setTimeout(() => {
            this._hideSubmenu(nestedSubmenu);
            this._updateAriaExpanded(nestedLink, false);
          }, 100);
        });

        // Click interactions for collapsed nested menus
        nestedLink.addEventListener('click', (e) => {
          if (this._shouldCollapseSubmenu(nestedSubmenu)) {
            e.preventDefault();
            const isCurrentlyVisible = nestedSubmenu.style.display === 'block';

            if (isCurrentlyVisible) {
              this._hideSubmenu(nestedSubmenu);
              this._updateAriaExpanded(nestedLink, false);
            } else {
              this._closeSiblingSubmenus(nestedItem);
              this._showSubmenu(nestedSubmenu);
              this._updateAriaExpanded(nestedLink, true);
            }
          }
        });

        // Keyboard navigation for nested items
        nestedLink.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight' || e.key === 'Enter') {
            e.preventDefault();
            // Close sibling submenus before opening this one
            this._closeSiblingSubmenus(nestedItem);
            this._showSubmenu(nestedSubmenu);
            this._updateAriaExpanded(nestedLink, true);
            const firstNestedSubmenuLink = nestedSubmenu.querySelector('.submenu__link');
            if (firstNestedSubmenuLink) {
              firstNestedSubmenuLink.focus();
            }
          } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            this._hideSubmenu(nestedSubmenu);
            this._updateAriaExpanded(nestedLink, false);
            nestedLink.focus();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            this._hideAllSubmenus();
            // Focus back to the original top-level link
            const topLevelLink = nestedItem.closest('.list-item').querySelector('.list-item__link');
            if (topLevelLink) {
              topLevelLink.focus();
            }
          }
        });
      });

      // Handle keyboard navigation within submenus
      const submenuContainers = item.querySelectorAll('.submenu');
      submenuContainers.forEach((submenuContainer) => {
        const submenuLinks = submenuContainer
          .querySelector('.submenu__list')
          .querySelectorAll(':scope > .submenu__item > .submenu__link');

        submenuLinks.forEach((submenuLink, index) => {
          submenuLink.addEventListener('keydown', (e) => {
            const submenuLinksArray = Array.from(submenuLinks);

            if (e.key === 'ArrowDown') {
              e.preventDefault();
              const nextIndex = (index + 1) % submenuLinksArray.length;
              submenuLinksArray[nextIndex].focus();
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              const prevIndex = index === 0 ? submenuLinksArray.length - 1 : index - 1;
              submenuLinksArray[prevIndex].focus();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              this._hideAllSubmenus();
              link.focus();
            } else if (e.key === 'ArrowLeft') {
              e.preventDefault();
              // Navigate back to parent submenu or top-level
              const parentSubmenu = submenuLink.closest('.submenu');
              const parentItem = parentSubmenu.closest('.submenu__item');

              if (parentItem) {
                // We're in a nested submenu, go back to parent
                const parentLink = parentItem.querySelector('.submenu__link');
                if (parentLink) {
                  // Hide current level submenu
                  const currentSubmenu = submenuLink.closest('.submenu');
                  this._hideSubmenu(currentSubmenu);
                  const currentLink = currentSubmenu.previousElementSibling;
                  if (currentLink) {
                    this._updateAriaExpanded(currentLink, false);
                  }
                  parentLink.focus();
                }
              } else {
                // We're in a top-level submenu, go back to main nav
                this._hideAllSubmenus();
                link.focus();
              }
            } else if (e.key === 'Tab') {
              // Allow normal tab navigation but close submenus when tabbing away
              setTimeout(() => {
                const focusedElement = document.activeElement;
                if (!this._mainNav.contains(focusedElement)) {
                  this._hideAllSubmenus();
                }
              }, 0);
            }
          });
        });
      });
    });

    // Close submenus when clicking outside
    document.addEventListener('click', (e) => {
      if (!this._mainNav.contains(e.target)) {
        this._hideAllSubmenus();
      }
    });

    // Global keyboard handlers for better accessibility
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        // Always close all submenus on escape
        this._hideAllSubmenus();
      }
    });

    // Handle focus leaving the menu entirely
    document.addEventListener('focusout', (e) => {
      // Use setTimeout to allow focus to move to the new element
      setTimeout(() => {
        const focusedElement = document.activeElement;
        // If focus has moved completely outside the main nav, close all submenus
        if (focusedElement && !this._mainNav.contains(focusedElement)) {
          this._hideAllSubmenus();
        }
      }, 0);
    });
  }

  /**
   * Close other top-level submenus
   */
  _closeOtherTopLevelSubmenus(currentItem) {
    const allTopLevelItems = this._mainNav.querySelectorAll('.list-item.has-submenu');
    allTopLevelItems.forEach((topLevelItem) => {
      if (topLevelItem !== currentItem) {
        const submenu = topLevelItem.querySelector('.submenu');
        const link = topLevelItem.querySelector('.list-item__link');
        this._hideSubmenu(submenu);
        this._updateAriaExpanded(link, false);
      }
    });
  }

  /**
   * Close sibling submenus at the same level
   */
  _closeSiblingSubmenus(currentItem) {
    const parentSubmenu = currentItem.closest('.submenu__list');
    if (!parentSubmenu) return;

    const siblingItems = parentSubmenu.querySelectorAll('.submenu__item.has-submenu');
    siblingItems.forEach((siblingItem) => {
      if (siblingItem !== currentItem) {
        const siblingSubmenu = siblingItem.querySelector('.submenu');
        const siblingLink = siblingItem.querySelector('.submenu__link');
        this._hideSubmenu(siblingSubmenu);
        this._updateAriaExpanded(siblingLink, false);
      }
    });
  }

  /**
   * Update aria-expanded attribute
   */
  _updateAriaExpanded(element, expanded) {
    if (element && element.hasAttribute('aria-haspopup')) {
      element.setAttribute('aria-expanded', expanded);
    }
  }

  /**
   * Hide all submenus
   */
  _hideAllSubmenus() {
    const submenus = this._mainNav.querySelectorAll('.submenu');
    const linksWithSubmenu = this._mainNav.querySelectorAll('[aria-haspopup="menu"]');

    submenus.forEach((submenu) => this._hideSubmenu(submenu));
    linksWithSubmenu.forEach((link) => this._updateAriaExpanded(link, false));
  }

  /**
   * Show submenu
   */
  _showSubmenu(submenu) {
    if (!submenu) return;

    // Check if submenu would go off-screen
    const shouldCollapse = this._shouldCollapseSubmenu(submenu);

    if (shouldCollapse) {
      // Use collapsible behavior
      submenu.classList.add('submenu--collapsed');
      submenu.style.position = 'static';
      submenu.style.opacity = '1';
      submenu.style.visibility = 'visible';
      submenu.style.transform = 'none';
      submenu.style.display = 'block';
    } else {
      // Use normal horizontal behavior
      submenu.classList.remove('submenu--collapsed');
      submenu.style.position = 'absolute';
      submenu.style.opacity = '1';
      submenu.style.visibility = 'visible';
      submenu.style.transform = 'translateY(0)';
      submenu.style.display = 'block';
    }

    // Make submenu items focusable
    const submenuLinks = submenu.querySelectorAll(
      ':scope > .submenu__list > .submenu__item > .submenu__link'
    );
    submenuLinks.forEach((link) => {
      link.setAttribute('tabindex', '0');
    });
  }

  /**
   * Hide submenu and all its child submenus
   */
  _hideSubmenu(submenu) {
    if (!submenu) return;

    if (submenu.classList.contains('submenu--collapsed')) {
      submenu.style.display = 'none';
    } else {
      submenu.style.opacity = '0';
      submenu.style.visibility = 'hidden';
      submenu.style.transform = 'translateY(-10px)';
    }

    // Make submenu items non-focusable
    const submenuLinks = submenu.querySelectorAll(
      ':scope > .submenu__list > .submenu__item > .submenu__link'
    );
    submenuLinks.forEach((link) => {
      link.setAttribute('tabindex', '-1');
    });

    // Also hide any nested submenus within this submenu
    const childSubmenus = submenu.querySelectorAll('.submenu');
    childSubmenus.forEach((childSubmenu) => {
      if (childSubmenu.classList.contains('submenu--collapsed')) {
        childSubmenu.style.display = 'none';
      } else {
        childSubmenu.style.opacity = '0';
        childSubmenu.style.visibility = 'hidden';
        childSubmenu.style.transform = 'translateY(-10px)';
      }

      // Make child submenu items non-focusable
      const childSubmenuLinks = childSubmenu.querySelectorAll(
        ':scope > .submenu__list > .submenu__item > .submenu__link'
      );
      childSubmenuLinks.forEach((link) => {
        link.setAttribute('tabindex', '-1');
      });

      // Reset aria-expanded for child submenu links
      const childLinks = childSubmenu
        .closest('.submenu__item')
        .querySelectorAll('[aria-haspopup="menu"]');
      childLinks.forEach((link) => this._updateAriaExpanded(link, false));
    });
  }

  /**
   * Check if submenu should collapse instead of showing horizontally
   */
  _shouldCollapseSubmenu(submenu) {
    if (!submenu) return false;

    const parentItem = submenu.closest('.list-item, .submenu__item');
    if (!parentItem) return false;

    const parentRect = parentItem.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    // Calculate where the submenu would appear
    const submenuWidth = 240; // min-width from CSS
    let wouldEndAt;

    // For level-1 submenus (under main nav items)
    if (submenu.classList.contains('submenu--level-1')) {
      wouldEndAt = parentRect.left + submenuWidth;
    } else {
      // For nested submenus (level-2, level-3)
      wouldEndAt = parentRect.right + submenuWidth;
    }

    // If submenu would go beyond viewport, use collapsed behavior
    // Add extra buffer for scrollbars and margins
    return wouldEndAt > viewportWidth - 40;
  }
  _enableScroll() {
    this._body.scrollTop = this._html.scrollTop = this._state.scrollTop;
    this._state.scrollTop = 0;
    enableBodyScroll(this._body);
    this._html.setAttribute('data-overflow', true);
  }

  _disableScroll() {
    this._state.scrollTop = this._body.scrollTop || this._html.scrollTop;
    disableBodyScroll(this._body);
    this._html.setAttribute('data-overflow', false);
  }

  closeSideMenu(element) {
    this._state.open = false;
    this.togglemenu(this, element);
  }

  togglemenu(t, el) {
    el.setAttribute('aria-expanded', t._state.open);
    t._target.setAttribute('data-open', t._state.open);
    t._main.setAttribute('data-side-open', t._state.open);
    clearAllBodyScrollLocks();
    if (t._state.open) {
      this._disableScroll();
    } else {
      this._enableScroll();
    }
  }
}

export default GispenMenu;
